<template>
  <div class="v2026-uic" :class="{ 'v2026-uic--card': registry.cardChrome }">
    <!-- STATIC markup — types 0 and 1. Type 0 is byte-identical to what it always was; type 1
         (splitContacts) drops the pencil from the ID row and swaps the phone/email pair for two
         labelled groups: the read-only system record, then the editable chat copy. -->
    <template v-if="!registry.dynamicRows">
      <div class="v2026-uic__row">
        <span class="v2026-uic__label">{{ registry.idLabel }}</span>
        <span v-if="idValue == null && noAccount" class="v2026-uic__value is-muted">ยังไม่มีบัญชี</span>
        <a v-else role="link" class="v2026-uic__link" :href="profileLink(idValue)" target="_blank">{{ idValue }}</a>
        <button v-if="canEdit && !registry.splitContacts" ref="editBtn" type="button" class="v2026-uic__icon-btn"
                :aria-label="registry.editAria" aria-haspopup="dialog" @click="displayModal()">
          <span v-if="registry.editIconMask" class="v2026-uic__mask v2026-uic__edit"
                aria-hidden="true" :style="maskStyle(registry.editIcon)"></span>
          <img v-else class="v2026-uic__edit-img" aria-hidden="true" :src="registry.editIcon" alt="">
        </button>
      </div>
      <template v-if="registry.splitContacts">
        <div class="v2026-uic__group">
          <div class="v2026-uic__row">
            <span class="v2026-uic__label">เบอร์ในระบบแม่บ้าน</span>
            <span class="v2026-uic__value" :class="{ 'is-muted': !nonEmpty(systemPhone) }">{{ nonEmpty(systemPhone) ? systemPhone : '-' }}</span>
          </div>
          <div class="v2026-uic__row">
            <span class="v2026-uic__label">อีเมลในระบบแม่บ้าน</span>
            <span class="v2026-uic__value v2026-uic__value--break" :class="{ 'is-muted': !nonEmpty(systemEmail) }">{{ nonEmpty(systemEmail) ? systemEmail : '-' }}</span>
          </div>
        </div>
        <div class="v2026-uic__group">
          <div class="v2026-uic__row">
            <span class="v2026-uic__label">เบอร์ติดต่อในแชท</span>
            <span class="v2026-uic__value" :class="{ 'is-muted': !nonEmpty(chatPhone) }">{{ nonEmpty(chatPhone) ? chatPhone : '-' }}</span>
            <button v-if="canEdit" ref="editBtn" type="button" class="v2026-uic__icon-btn"
                    :aria-label="registry.editAria" aria-haspopup="dialog" @click="displayModal()">
              <span v-if="registry.editIconMask" class="v2026-uic__mask v2026-uic__edit"
                    aria-hidden="true" :style="maskStyle(registry.editIcon)"></span>
              <img v-else class="v2026-uic__edit-img" aria-hidden="true" :src="registry.editIcon" alt="">
            </button>
          </div>
          <div class="v2026-uic__row">
            <span class="v2026-uic__label">อีเมลติดต่อในแชท</span>
            <span class="v2026-uic__value v2026-uic__value--break" :class="{ 'is-muted': !nonEmpty(chatEmail) }">{{ nonEmpty(chatEmail) ? chatEmail : '-' }}</span>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="v2026-uic__row">
          <span class="v2026-uic__label">Phone:</span>
          <span class="v2026-uic__value">{{ phone }}</span>
        </div>
        <div class="v2026-uic__row">
          <span class="v2026-uic__label">Email:</span>
          <span class="v2026-uic__value v2026-uic__value--break"
                :class="{ 'is-muted': !hasEmail }"
                :title="hasEmail ? null : 'ยังไม่มีข้อมูลในระบบ'">{{ email }}</span>
        </div>
      </template>
    </template>

    <!-- DYNAMIC rows — the applicant panel, where a row with no data must not be in the DOM. -->
    <template v-else>
      <div class="v2026-uic__row" v-for="(row, i) in visibleRows" :key="row.key">
        <span class="v2026-uic__label">{{ row.label }}</span>
        <a v-if="row.href" role="link" class="v2026-uic__link" :href="row.href"
           target="_blank">{{ row.value }}</a>
        <span v-else class="v2026-uic__value v2026-uic__value--break">{{ row.value }}</span>
        <button v-if="i === 0 && canEdit" ref="editBtn" type="button" class="v2026-uic__icon-btn"
                :aria-label="registry.editAria" aria-haspopup="dialog" @click="displayModal()">
          <span v-if="registry.editIconMask" class="v2026-uic__mask v2026-uic__edit"
                aria-hidden="true" :style="maskStyle(registry.editIcon)"></span>
          <img v-else class="v2026-uic__edit-img" aria-hidden="true" :src="registry.editIcon" alt="">
        </button>
      </div>
      <!-- No rows means no pencil, so an empty card still needs a way into the edit modal. -->
      <button v-if="!visibleRows.length && canEdit && registry.addLabel" ref="editBtn" type="button"
              class="v2026-uic__add" :aria-label="registry.addLabel" aria-haspopup="dialog"
              @click="displayModal()">
        <span class="v2026-uic__add-ico" aria-hidden="true" :style="maskStyle(plusIcon)"></span>
        <span>{{ registry.addLabel }}</span>
      </button>
    </template>

    <BaseModal v-if="convCustomer" :open="showContactEdit" max-width="480px"
               :position="['top', 'center']" :modalStyle="['margin-top:10%']"
               @close="closeModal()">
      <div class="v2026-uic-ce" role="dialog" aria-modal="true" aria-labelledby="v2026-uic-ce-title">
        <div class="v2026-uic-ce__head">
          <span id="v2026-uic-ce-title" class="v2026-uic-ce__title">แก้ไขข้อมูลผู้ติดต่อ</span>
          <button type="button" class="v2026-uic-ce__close" aria-label="ปิด"
                  @click="closeModal()">×</button>
        </div>
        <div class="v2026-uic-ce__body">
          <div class="v2026-uic-ce__group">
            <label class="v2026-uic-ce__label" for="v2026-uic-ce-email">อีเมล</label>
            <input id="v2026-uic-ce-email" type="email" class="v2026-uic-ce__input"
                   placeholder="อีเมล" v-model="contactDraft.email">
          </div>
          <div class="v2026-uic-ce__group">
            <label class="v2026-uic-ce__label" for="v2026-uic-ce-phone">เบอร์โทร</label>
            <input id="v2026-uic-ce-phone" type="text" class="v2026-uic-ce__input"
                   placeholder="เบอร์โทร" v-model="contactDraft.phone">
          </div>
          <p v-if="registry.contactHint" class="v2026-uic-ce__hint">{{ registry.contactHint }}</p>
          <p v-if="registry.inlineSaveFeedback && saveError" class="v2026-uic-ce__error"
             role="alert">* บันทึกไม่สำเร็จ กรุณาลองใหม่</p>
          <p v-if="registry.inlineSaveFeedback && saveOk" class="v2026-uic-ce__ok"
             role="alert">บันทึกข้อมูลสำเร็จ</p>
          <div class="v2026-uic-ce__actions">
            <button type="button" class="v2026-uic-ce__save"
                    :disabled="registry.inlineSaveFeedback && saving"
                    @click="saveCustomerData">บันทึก</button>
          </div>
        </div>
      </div>
    </BaseModal>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import BaseModal from '@/components/coversation/BaseModal.vue'

// How long the inline success line stays up before the modal closes itself.
const SUCCESS_CLOSE_DELAY = 900

// Every per-panel difference, and the ONLY ones:
// 1. Profile link — the professional URL has a /profile suffix, so the base is split into
//    linkBase + linkSuffix rather than inferred from the customer one.
// 2. Pencil glyph — stroke-based <img> vs CSS-mask span. Different asset AND paint mechanism.
// 3. editAria — the customer's is FROZEN verbatim even though it is inaccurate (the button
//    edits email/phone, not the ID). Do not "fix" it; the zero-change constraint owns it.
// 4. cardChrome — rounded card vs flat.
// 5. contactHint / inlineSaveFeedback — professional-only, empty for the customer.
// 6. splitContacts — professional-only. The system record and the chat copy are shown as two
//    labelled groups, so staff can tell which values the pencil edits.
const TYPE_REGISTRY = {
  0: {
    name: 'customer',
    idLabel: 'User ID:',
    editAria: 'แก้ไข User ID',
    linkBase: 'https://admin-test.beneat.co/user/',
    linkSuffix: '',
    editIcon: require('@/assets/icons2026/pencil-edit.svg'),
    editIconMask: false,
    cardChrome: true,
    contactHint: '',
    inlineSaveFeedback: false
  },
  1: {
    name: 'professional',
    idLabel: 'Provider ID:',
    editAria: 'แก้ไขข้อมูลผู้ติดต่อ',
    linkBase: 'https://admin-test.beneat.co/professional/',
    linkSuffix: '/profile',
    editIcon: require('@/assets/icons2026/edit.svg'),
    editIconMask: true,
    cardChrome: false,
    contactHint: '* ข้อมูลนี้จะบันทึกในข้อมูลผู้ติดต่อของแชท ไม่เปลี่ยนข้อมูลในระบบแม่บ้าน',
    inlineSaveFeedback: true,
    splitContacts: true
  },
  // Applicant: linkBase/linkSuffix are unused — the register-report href keys on id_card_number
  // or tel, so the panel resolves it and passes it in.
  2: {
    name: 'applicant',
    idLabel: 'Applicant ID:',
    editAria: 'แก้ไขข้อมูลผู้ติดต่อ',
    linkBase: '',
    linkSuffix: '',
    editIcon: require('@/assets/icons2026/edit.svg'),
    editIconMask: true,
    cardChrome: false,
    contactHint: '* ข้อมูลนี้จะบันทึกในข้อมูลผู้ติดต่อของแชท ไม่เปลี่ยนข้อมูลใบสมัคร',
    inlineSaveFeedback: true,
    dynamicRows: true,
    addLabel: 'เพิ่มเบอร์โทร / อีเมล'
  }
}

const REGISTRY_DEFAULTS = {
  dynamicRows: false,
  addLabel: '',
  splitContacts: false
}

export default {
  name: 'user-info-card-v2026',
  components: {
    BaseModal
  },
  props: {
    type: {
      type: Number,
      default: 0
    },
    idValue: {
      type: [Number, String],
      default: null
    },
    phone: {
      type: String,
      default: null
    },
    email: {
      type: String,
      default: null
    },
    // Type 1 only: the system record (read-only here) and the chat copy (edited via the modal).
    systemPhone: {
      type: String,
      default: null
    },
    systemEmail: {
      type: String,
      default: null
    },
    chatPhone: {
      type: String,
      default: null
    },
    chatEmail: {
      type: String,
      default: null
    },
    hasEmail: {
      type: Boolean,
      default: true
    },
    convCustomer: {
      type: Object,
      default: null
    },
    conversationId: {
      type: [Number, String],
      default: null
    },
    idHref: {
      type: String,
      default: null
    },
    dynamicRows: {
      type: Boolean,
      default: null
    },
    canEdit: {
      type: Boolean,
      default: true
    },
    // Caller has confirmed there is no BeNeat account (not just "still loading") — the empty
    // User ID then reads "ยังไม่มีบัญชี". The register action itself lives outside this card.
    noAccount: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      showContactEdit: false, // Modal B visibility
      saving: false, // request in flight → M3 disables บันทึก (type 1 only)
      saveError: false, // M1 inline danger line (type 1 only)
      saveOk: false, // M2 inline success line (type 1 only)
      closeTimer: null, // M2 delayed-close handle
      // Local copy the edit modal writes to. convCustomer is only updated after a successful save,
      // so typed-but-unsaved values never reach the shared conversation store.
      contactDraft: { email: null, phone: null },
      plusIcon: require('@/assets/icons2026/plus.svg')
    }
  },
  computed: {
    registry () {
      const row = Object.assign({}, REGISTRY_DEFAULTS, TYPE_REGISTRY[this.type] || TYPE_REGISTRY[0])
      if (this.dynamicRows !== null) row.dynamicRows = this.dynamicRows
      return row
    },
    visibleRows () {
      const rows = []
      if (this.idValue != null) {
        rows.push({
          key: 'id',
          label: this.registry.idLabel,
          value: this.idValue,
          href: this.idHref || this.profileLink(this.idValue)
        })
      }
      if (this.nonEmpty(this.phone)) {
        rows.push({ key: 'phone', label: 'Phone:', value: this.phone, href: null })
      }
      if (this.nonEmpty(this.email)) {
        rows.push({ key: 'email', label: 'Email:', value: this.email, href: null })
      }
      return rows
    }
  },
  watch: {
    convCustomer: 'resetModalState',
    conversationId: 'resetModalState'
  },
  beforeDestroy () {
    this.clearCloseTimer()
  },
  methods: {
    ...mapActions('customer', ['saveCustomer']),
    ...mapActions('conversation', ['searchCustomer']),
    // idHref, when the caller supplies it, wins over this. An empty linkBase with no idHref
    // yields null, so the row renders as plain text rather than a link to the bare id.
    profileLink (id) {
      if (this.idHref) return this.idHref
      if (!this.registry.linkBase) return null
      return id !== null ? `${this.registry.linkBase}${id}${this.registry.linkSuffix}` : null
    },
    nonEmpty (v) {
      return v != null && String(v).trim() !== ''
    },
    displayModal () {
      this.saveError = false
      this.saveOk = false
      // Prefill from the chat-side contact copy, same as before (not the value the card displays)
      const conv = this.convCustomer || {}
      this.contactDraft = {
        email: conv.email,
        phone: conv.phone
      }
      this.showContactEdit = true
    },
    resetModalState () {
      this.clearCloseTimer()
      this.showContactEdit = false
      this.saving = false
      this.saveError = false
      this.saveOk = false
    },
    closeModal () {
      this.clearCloseTimer()
      this.showContactEdit = false
      this.saveOk = false
      this.$nextTick(() => {
        const ref = this.$refs.editBtn
        const btn = Array.isArray(ref) ? ref[0] : ref
        if (btn && btn.focus) btn.focus()
      })
    },
    clearCloseTimer () {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer)
        this.closeTimer = null
      }
    },
    async saveCustomerData () {
      if (this.convCustomer.name) {
        this.saving = true
        this.saveError = false
        this.saveOk = false
        try {
          const customer = {
            id: this.convCustomer.id,
            name: this.convCustomer.name,
            phone: this.contactDraft.phone,
            email: this.contactDraft.email
          }
          const submitData = {
            customer: customer,
            conversation_id: this.conversationId
          }
          await this.saveCustomer(submitData)
          // Saved: promote the draft into the shared customer object (the panel reads from it)
          this.$set(this.convCustomer, 'phone', this.contactDraft.phone)
          this.$set(this.convCustomer, 'email', this.contactDraft.email)
          const params = {
            phone: this.convCustomer.phone,
            email: this.convCustomer.email,
            line_user_id: this.convCustomer.lid,
            customer_id: this.convCustomer.id,
            beneat_professional_id: this.convCustomer.beneat_professional_id
          }
          await this.searchCustomer(params)
          if (this.registry.inlineSaveFeedback) {
            this.saveOk = true
            this.clearCloseTimer()
            this.closeTimer = setTimeout(() => {
              this.closeTimer = null
              this.closeModal()
            }, SUCCESS_CLOSE_DELAY)
          } else {
            this.closeModal()
          }
          this.$emit('notify', { variant: 'success', title: 'บันทึกข้อมูลสำเร็จ', message: 'ทำการบันทึกข้อมูลสำเร็จ' })
        } catch (e) {
          this.saveError = true
          this.$emit('notify', { variant: 'danger', title: 'เกิดข้อผิดพลาด', message: 'บันทึกไม่สำเร็จ กรุณาลองใหม่' })
        } finally {
          this.saving = false
        }
      } else {
        this.$swal({ text: 'กรุณากรอกชื่อ' })
      }
    },
    maskStyle (url) {
      return {
        '-webkit-mask-image': 'url(' + url + ')',
        'mask-image': 'url(' + url + ')'
      }
    }
  }
}
</script>

<style scoped>
.v2026-uic {
  --v2026-uic-link: #007bff;
  --v2026-uic-surface-hover: #e9ecf1;
  --v2026-uic-text: #101828;
  --v2026-uic-text-secondary: #667085;
  --v2026-uic-text-muted: #98a2b3;
  --v2026-uic-icon: #475467;
  --v2026-uic-main: #31C1D7;

  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0 16px;
}

.v2026-uic--card {
  margin: 0 16px;
  padding: 12px 16px;
  background: #F8FAFC;
  border-radius: 8px;
}

.v2026-uic__row {
  display: flex;
  align-items: center;
  gap: 8px;
}
/* Type 1 contact groups: rows inside a group stack tight, groups are separated by a gap. */
.v2026-uic__group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.v2026-uic__group + .v2026-uic__group {
  margin-top: 8px;
}
.v2026-uic__label {
  color: var(--v2026-uic-text-secondary);
  font-size: 14px;
  font-weight: 400;
}
.v2026-uic__value {
  color: var(--v2026-uic-text);
  font-size: 14px;
  font-weight: 500;
}
.v2026-uic__value--break {
  word-break: break-all;
}

.v2026-uic__value.is-muted {
  color: var(--v2026-uic-text-muted);
  font-weight: 400;
}

.v2026-uic__link {
  color: var(--v2026-uic-link);
  text-decoration: none;
  font-size: 14px;
}
.v2026-uic__link:hover {
  text-decoration: underline;
}
.v2026-uic__link:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
  border-radius: 3px;
}

.v2026-uic__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
}
.v2026-uic__icon-btn:hover {
  background: var(--v2026-uic-surface-hover);
}
.v2026-uic__icon-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}

.v2026-uic__mask {
  display: inline-block;
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
  background-color: var(--v2026-uic-icon);
}
.v2026-uic__edit {
  width: 16px;
  height: 16px;
  background-color: var(--v2026-uic-icon);
}

.v2026-uic__add {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 8px 0 6px;
  border: 0;
  background: transparent;
  border-radius: 999px;
  color: var(--v2026-uic-main);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.v2026-uic__add:hover {
  background: rgba(49, 193, 215, 0.12);
}
.v2026-uic__add:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}
.v2026-uic__add-ico {
  display: inline-block;
  width: 14px;
  height: 14px;
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
  background-color: currentColor;
}

.v2026-uic__edit-img {
  width: 16px;
  height: 16px;
  object-fit: contain;
  display: block;
}

.v2026-uic-ce__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #FFFFFF;
  border-bottom: 1px solid #EBEDF0;
  padding: 16px 24px;
}
.v2026-uic-ce__body {
  padding: 20px 24px;
}
.v2026-uic-ce__title {
  font-size: 16px;
  font-weight: 600;
  color: #1F2937;
}
.v2026-uic-ce__close {
  border: none;
  background: transparent;
  padding: 0;
  font-size: 20px;
  line-height: 1;
  color: #66696C;
  cursor: pointer;
}
.v2026-uic-ce__group + .v2026-uic-ce__group {
  margin-top: 16px;
}
.v2026-uic-ce__label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  color: #66696C;
}
.v2026-uic-ce__input {
  width: 100%;
  height: 44px;
  padding: 0 12px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  font-size: 14px;
  color: #1F2937;
  outline: none;
}
.v2026-uic-ce__input:focus {
  border-color: var(--v2026-uic-main);
}
.v2026-uic-ce__actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}
.v2026-uic-ce__save {
  height: 44px;
  padding: 0 24px;
  border: none;
  border-radius: 8px;
  background: var(--v2026-uic-main);
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}
.v2026-uic-ce__save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.v2026-uic-ce__hint {
  margin: 16px 0 0;
  font-size: 12px;
  color: #9CA3AF;
}
.v2026-uic-ce__error {
  margin: 8px 0 0;
  font-size: 12px;
  color: #F04438;
}
.v2026-uic-ce__ok {
  margin: 8px 0 0;
  font-size: 12px;
  color: #12B76A;
}
</style>
